<template>
  <div class="tw-w-full">
    <div class="tw-flex tw-w-full" :class="{ 'tw-cursor-grab': freeMode }">
      <div class="tw-mr-2 tw-flex tw-items-center" v-if="isNavigationShown">
        <button
          :class="
            twMerge(
              'tw-grid tw-h-10 tw-w-10 tw-place-items-center tw-rounded-full tw-border-2 tw-border-neutral-200 tw-bg-neutral-50 focus-visible:tw-outline-none focus-visible:tw-ring focus-visible:tw-ring-neutral-300 disabled:tw-cursor-not-allowed',
              arrowClasses,
            )
          "
          @click="previousSlide"
          :disabled="isBeginning"
          test_id="carousel-button-left"
        >
          <ArrowLeftIcon :class="twMerge('tw-h-3 tw-w-2', arrowIconClasses)" />
        </button>
      </div>

      <swiper-container
        ref="swiperRef"
        init="false"
        @slidechange="handleSlideChange"
        class="tw-w-full"
      >
        <swiper-slide v-for="(slideData, index) in slidesData" :key="index">
          <slot name="slide" :slide-data="slideData" :index="index" />
        </swiper-slide>
      </swiper-container>

      <div class="tw-ml-2 tw-flex tw-items-center" v-if="isNavigationShown">
        <button
          :class="
            twMerge(
              'tw-grid tw-h-10 tw-w-10 tw-place-items-center tw-rounded-full tw-border-2 tw-border-neutral-200 tw-bg-neutral-50 focus-visible:tw-outline-none focus-visible:tw-ring focus-visible:tw-ring-neutral-300 disabled:tw-cursor-not-allowed',
              arrowClasses,
            )
          "
          @click="nextSlide"
          :disabled="isEnd"
          test_id="carousel-button-right"
        >
          <ArrowRightIcon :class="twMerge('tw-h-3 tw-w-2', arrowIconClasses)" />
        </button>
      </div>
    </div>
    <div
      v-if="isPaginationEnabled"
      class="tw-mt-4 tw-flex tw-w-full tw-justify-center"
    >
      <div class="swiper-pagination" />
    </div>
  </div>
</template>

<script setup>
import { register } from 'swiper/element'
import ArrowLeftIcon from '@/assets/images/icons/arrow-left.svg'
import ArrowRightIcon from '@/assets/images/icons/arrow-right.svg'
import { computed, onMounted, ref, watch } from 'vue'
import { twMerge } from 'tailwind-merge'

register()

const props = defineProps({
  slidesPerView: {
    type: Number,
    default: 1,
  },
  spaceBetween: {
    type: Number,
    default: 20,
  },
  freeMode: {
    type: Boolean,
    default: false,
  },
  grabCursor: {
    type: Boolean,
    default: false,
  },
  breakpoints: {
    type: Object,
    default: () => {},
  },
  isNavigationEnabled: {
    type: Boolean,
    default: true,
  },
  slidesData: {
    type: Array,
    required: true,
  },
  isHidingNavigationDisabled: {
    type: Boolean,
    default: false,
  },
  arrowClasses: {
    type: [String, Array],
    default: '',
  },
  arrowIconClasses: {
    type: [String, Array],
    default: '',
  },
  isPaginationEnabled: {
    type: Boolean,
    default: false,
  },
  currentSlideIndex: {
    type: Number,
    default: 0,
  },
})

const swiperRef = ref(null)
const slidesLength = ref(0)
const transitionSpeedMs = ref(500)
const swiperOption = ref({
  slidesPerView: props.slidesPerView,
  spaceBetween: props.spaceBetween,
  freeMode: props.freeMode,
  grabCursor: props.grabCursor,
  breakpoints: props.breakpoints,
  pagination: {
    el: '.swiper-pagination',
    clickable: true,
    dynamicBullets: false,
  },
})
const isBeginning = ref(true)
const isEnd = ref(false)

const isNavigationShown = computed(() => {
  // By default, navigation is hidden on mobile phones
  if (!props.isHidingNavigationDisabled && window.innerWidth < 576) {
    return false
  }

  const isMoreDataThanSlidesPerView =
    props.slidesData.length > slidesLength.value
  return props.isNavigationEnabled && isMoreDataThanSlidesPerView
})

const handleSlideChange = () => {
  updateNavigationStatus()
}

const previousSlide = () => {
  swiperRef.value.swiper.slidePrev(transitionSpeedMs.value)
}

const nextSlide = () => {
  swiperRef.value.swiper.slideNext(transitionSpeedMs.value)
}

const updateNavigationStatus = () => {
  isBeginning.value = swiperRef.value.swiper.isBeginning
  isEnd.value = swiperRef.value.swiper.isEnd
}

const updateActiveSlidesPerView = () => {
  const breakpoints = Object.keys(props.breakpoints).map(parseFloat)
  breakpoints
    .sort((a, b) => a - b)
    .forEach((breakpoint) => {
      if (window.innerWidth > breakpoint) {
        slidesLength.value = props.breakpoints[breakpoint].slidesPerView
      }
    })
}

watch(
  () => props.currentSlideIndex,
  (value) => {
    swiperRef.value.swiper.slideTo(value, transitionSpeedMs.value)
  },
)

onMounted(() => {
  Object.assign(swiperRef.value, swiperOption.value)
  swiperRef.value.initialize()
  updateActiveSlidesPerView()
  updateNavigationStatus()
})

defineExpose({
  isBeginning,
  isEnd,
  previousSlide,
  nextSlide,
})
</script>

<style>
.swiper-container {
  flex-grow: 1;
}

.swiper-container {
  z-index: 0 !important;
}

.swiper-pagination {
  @apply tw-flex tw-items-center;
}

.swiper-pagination-bullet {
  @apply tw-m-1.5 tw-bg-neutral-200 tw-opacity-100;
}

.swiper-pagination-bullet-active {
  @apply tw-h-3 tw-w-3 tw-bg-neutral-400;
}
</style>
